import { useContext, useEffect, useMemo, useState, memo } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import useRole from '../../hooks/useRole';
import useRight from '../../hooks/useRight';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  FormControlLabel,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const CATEGORY_COLUMNS = [
  { key: 'privateStandard', label: 'Privat Standard' },
  { key: 'privateExpanded', label: 'Privat Erweitert' },
  { key: 'companyStandard', label: 'Firma Standard' },
  { key: 'companyExpanded', label: 'Firma Erweitert' },
  { key: 'counterStandard', label: 'Kasse Standard' },
  { key: 'counterExpanded', label: 'Kasse Erweitert' },
];

const buildInitialAssigned = r => ({
  privateStandard: r?.privateStandard || [],
  privateExpanded: r?.privateExpanded || [],
  companyStandard: r?.companyStandard || [],
  companyExpanded: r?.companyExpanded || [],
  counterStandard: r?.counterStandard || [],
  counterExpanded: r?.counterExpanded || [],
});

// Cache across remounts to avoid repeated refetching (e.g. due to React StrictMode or routing quirks)
let cachedRightsTree = null; // Array
let cachedRightsTreePromise = null;
const cachedRolesById = new Map(); // roleId -> role
const cachedRolePromisesById = new Map(); // roleId -> Promise<role>

function RoleRightsMatrix() {
  const authCtx = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const roleId = searchParams.get('roleId');

  // permissions
  if (!authCtx.checkRight('role:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const { getRoleById, updateRoleById, isLoading: roleLoading } = useRole();
  const { getRightsTree, isLoading: rightsLoading } = useRight();

  const [role, setRole] = useState(() => (roleId ? cachedRolesById.get(roleId) || null : null));
  const [tree, setTree] = useState(() => (Array.isArray(cachedRightsTree) ? cachedRightsTree : []));
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [initialAssigned, setInitialAssigned] = useState(() =>
    roleId && cachedRolesById.has(roleId) ? buildInitialAssigned(cachedRolesById.get(roleId)) : null
  );

  const canUpdate = authCtx.checkRight('role:update');

  useEffect(() => {
    if (!roleId) return;
    let isActive = true;

    const loadData = async () => {
      try {
        const rolePromise =
          cachedRolesById.has(roleId)
            ? Promise.resolve(cachedRolesById.get(roleId))
            : cachedRolePromisesById.get(roleId) ||
              (async () => {
                const p = getRoleById(roleId);
                cachedRolePromisesById.set(roleId, p);
                const r = await p;
                cachedRolesById.set(roleId, r);
                cachedRolePromisesById.delete(roleId);
                return r;
              })();

        if (!cachedRightsTreePromise && !Array.isArray(cachedRightsTree)) {
          cachedRightsTreePromise = (async () => {
            const t = await getRightsTree();
            cachedRightsTree = Array.isArray(t) ? t : [];
            cachedRightsTreePromise = null;
            return cachedRightsTree;
          })().catch(err => {
            cachedRightsTreePromise = null;
            throw err;
          });
        }

        const [r, t] = await Promise.all([
          rolePromise,
          Array.isArray(cachedRightsTree) ? Promise.resolve(cachedRightsTree) : cachedRightsTreePromise,
        ]);
        if (!isActive) return;
        setRole(r);
        setTree(Array.isArray(t) ? t : []);
        setInitialAssigned(buildInitialAssigned(r));
      } catch (e) {
        if (!isActive) return;
        toast.error('Fehler beim Laden der Rolle oder Rechte');
      }
    };

    loadData();

    return () => {
      isActive = false;
    };
  }, [roleId]);

  const rightsRows = useMemo(() => {
    const rows = [];
    tree.forEach(domain => {
      rows.push({
        type: 'domain',
        name: domain.name,
        key: domain.name,
        description: domain.description,
        dangerous: !!domain.dangerous,
      });
      domain.actions.forEach(action => {
        const actionKey = `${domain.name}:${action.key}`;
        rows.push({
          type: 'action',
          name: actionKey,
          key: actionKey,
          description: action.description,
          dangerous: !!action.dangerous,
        });
        action.subActions.forEach(sa => {
          const subKey = `${actionKey}:${sa.key}`;
          rows.push({
            type: 'sub',
            name: subKey,
            key: subKey,
            description: sa.description,
            dangerous: !!sa.dangerous,
          });
        });
      });
    });
    const term = (filter || '').toLowerCase();
    if (!term) return rows;
    return rows.filter(
      r => r.name.toLowerCase().includes(term) || (r.description || '').toLowerCase().includes(term)
    );
  }, [tree, filter]);

  // Build fast lookup sets for membership checks per column
  const setsByCol = useMemo(() => {
    if (!role) return null;
    return {
      privateStandard: new Set(role.privateStandard || []),
      privateExpanded: new Set(role.privateExpanded || []),
      companyStandard: new Set(role.companyStandard || []),
      companyExpanded: new Set(role.companyExpanded || []),
      counterStandard: new Set(role.counterStandard || []),
      counterExpanded: new Set(role.counterExpanded || []),
    };
  }, [
    role?.privateStandard,
    role?.privateExpanded,
    role?.companyStandard,
    role?.companyExpanded,
    role?.counterStandard,
    role?.counterExpanded,
  ]);

  const toggleRight = (colKey, rightKey) => {
    if (!canUpdate) return;
    setRole(prev => {
      if (!prev) return prev;
      const current = new Set(prev[colKey] || []);
      if (current.has(rightKey)) current.delete(rightKey);
      else current.add(rightKey);
      return { ...prev, [colKey]: Array.from(current) };
    });
  };

  const hasChanges = useMemo(() => {
    if (!role || !initialAssigned) return false;
    return CATEGORY_COLUMNS.some(c => {
      const a = new Set(role[c.key] || []);
      const b = new Set(initialAssigned[c.key] || []);
      if (a.size !== b.size) return true;
      for (const v of a) if (!b.has(v)) return true;
      return false;
    });
  }, [role, initialAssigned]);

  const handleSave = async () => {
    if (!role || !role._id) return;
    setSaving(true);
    try {
      const payload = CATEGORY_COLUMNS.reduce((acc, c) => {
        acc[c.key] = role[c.key] || [];
        return acc;
      }, {});
      const updated = await updateRoleById(role._id, payload);
      setRole(updated);
      setInitialAssigned({
        privateStandard: updated?.privateStandard || [],
        privateExpanded: updated?.privateExpanded || [],
        companyStandard: updated?.companyStandard || [],
        companyExpanded: updated?.companyExpanded || [],
        counterStandard: updated?.counterStandard || [],
        counterExpanded: updated?.counterExpanded || [],
      });
      toast.success('Rolle erfolgreich gespeichert');
    } catch (e) {
      toast.error('Fehler beim Speichern der Rolle');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!initialAssigned || !role) return;
    setRole(prev => ({ ...prev, ...initialAssigned }));
  };

  // Memoized row to minimize re-renders; compares per-cell booleans
  const RowItem = useMemo(
    () =>
      memo(
        function RowItem({ row, canUpdate, onToggle, checks }) {
          return (
            <TableRow
              hover
              sx={
                row.dangerous
                  ? {
                      bgcolor: theme =>
                        alpha(
                          theme.palette.error.main,
                          theme.palette.mode === 'light' ? 0.06 : 0.08
                        ),
                      borderLeft: theme => `3px solid ${theme.palette.error.main}`,
                    }
                  : undefined
              }
            >
              <TableCell>
                <Typography variant={row.type === 'domain' ? 'subtitle2' : 'body2'}>
                  {row.name}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant='body2' color='text.secondary'>
                  {row.description || ''}
                </Typography>
              </TableCell>
              {CATEGORY_COLUMNS.map(c => (
                <TableCell key={c.key} align='center'>
                  <Checkbox
                    size='small'
                    checked={checks[c.key]}
                    onChange={() => onToggle(c.key, row.key)}
                    disabled={!canUpdate}
                  />
                </TableCell>
              ))}
            </TableRow>
          );
        },
        (prev, next) => {
          if (prev.canUpdate !== next.canUpdate) return false;
          if (prev.row.key !== next.row.key) return false;
          if (prev.row.dangerous !== next.row.dangerous) return false;
          for (const c of CATEGORY_COLUMNS) {
            if (prev.checks[c.key] !== next.checks[c.key]) return false;
          }
          return true;
        }
      ),
    []
  );

  return (
    <Box sx={{ p: 2, ...viewRootTextPrimarySx }}>
      <Box display='flex' alignItems='center' gap={1} mb={2}>
        <IconButton onClick={() => window.history.back()} size='small'>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h6' fontWeight={600}>
          Rechte-Matrix {role?.readableName ? `– ${role.readableName}` : ''}
        </Typography>
        <TextField
          size='small'
          placeholder='Rechte/ Beschreibung filtern...'
          defaultValue={filter}
          onChange={e => setFilter(e.target.value)}
          sx={{ ml: 2, width: { xs: 180, sm: 280 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        <Box ml='auto' display='flex' gap={1}>
          <Tooltip title='Änderungen verwerfen'>
            <span>
              <IconButton onClick={handleReset} disabled={!hasChanges} color='inherit'>
                <UndoIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title='Speichern'>
            <span>
              <Button
                variant='contained'
                color='success'
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!canUpdate || !hasChanges || saving}
              >
                Speichern
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Paper>
        {(roleLoading || rightsLoading || !role) && (
          <Box
            sx={{
              ...viewRootTextPrimarySx,
              p: 4,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {role && tree && (
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 320 }}>Recht</TableCell>
                <TableCell>Beschreibung</TableCell>
                {CATEGORY_COLUMNS.map(c => (
                  <TableCell key={c.key} align='center'>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rightsRows.map(row => {
                const checks = setsByCol
                  ? {
                      privateStandard: setsByCol.privateStandard.has(row.key),
                      privateExpanded: setsByCol.privateExpanded.has(row.key),
                      companyStandard: setsByCol.companyStandard.has(row.key),
                      companyExpanded: setsByCol.companyExpanded.has(row.key),
                      counterStandard: setsByCol.counterStandard.has(row.key),
                      counterExpanded: setsByCol.counterExpanded.has(row.key),
                    }
                  : {
                      privateStandard: false,
                      privateExpanded: false,
                      companyStandard: false,
                      companyExpanded: false,
                      counterStandard: false,
                      counterExpanded: false,
                    };
                return (
                  <RowItem
                    key={row.key}
                    row={row}
                    canUpdate={canUpdate}
                    onToggle={toggleRight}
                    checks={checks}
                  />
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}

export default RoleRightsMatrix;
